import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export default function Sessions() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [acct, setAcct] = useState([]);
  const [structures, setStructures] = useState([]);
  const [testCode, setTestCode] = useState("");
  const [testStruct, setTestStruct] = useState("");

  const load = () => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/sessions${p}`).then((r) => setSessions(r.data));
    api.get(`/accounting${p}`).then((r) => setAcct(r.data));
  };
  useEffect(() => { load(); api.get("/structures").then((r) => { setStructures(r.data); setTestStruct(effectiveStructure || r.data[0]?.id || ""); }); }, [effectiveStructure]);

  const simulate = async () => {
    try {
      const { data } = await api.post("/radius/authenticate", { structure_id: testStruct, code: testCode });
      if (data.result === "accept") toast.success("✅ " + t("sess.accept"));
      else toast.error("⛔ " + t("sess.reject") + ": " + data.reason);
      setTestCode(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="sessions-page">
      <PageHeader title={t("sess.title")} />

      {/* Simulator */}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-5 mb-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-400"><Zap className="h-4 w-4" /> {t("sess.simulate")}</div>
        <div className="flex-1 grid grid-cols-2 gap-3">
          <select data-testid="sim-structure-select" value={testStruct} onChange={(e) => setTestStruct(e.target.value)} className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm">
            {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input data-testid="sim-code-input" placeholder={t("wifi.code")} value={testCode} onChange={(e) => setTestCode(e.target.value.toUpperCase())} className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button data-testid="sim-auth-btn" onClick={simulate} className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold">RADIUS Auth</button>
      </div>

      <Tabs defaultValue="live">
        <TabsList className="bg-[#111827] border border-[#1F2937]">
          <TabsTrigger value="live" data-testid="tab-live" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">{t("sess.live")}</TabsTrigger>
          <TabsTrigger value="acct" data-testid="tab-accounting" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">{t("sess.accounting")}</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="mt-4">
          <TableWrap headers={[t("sess.username"), t("sess.mac"), t("sess.ip"), t("sess.start"), t("sess.up"), t("sess.down"), t("common.status")]}>
            {sessions.map((s) => (
              <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-cyan-400">{s.username}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.mac}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.ip}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{new Date(s.start).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono">{s.upload_mb}</td>
                <td className="px-4 py-3 font-mono">{s.download_mb}</td>
                <td className="px-4 py-3"><StatusBadge status={s.active ? "online" : "inactive"} label={s.active ? "LIVE" : "closed"} /></td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">{t("common.none")}</td></tr>}
          </TableWrap>
        </TabsContent>

        <TabsContent value="acct" className="mt-4">
          <TableWrap headers={[t("sess.username"), t("sess.result"), t("sess.message"), t("sess.mac"), t("res.created")]}>
            {acct.map((a) => (
              <tr key={a.id} data-testid={`acct-row-${a.id}`} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-cyan-400">{a.username}</td>
                <td className="px-4 py-3"><StatusBadge status={a.result} label={a.result === "accept" ? t("sess.accept") : t("sess.reject")} /></td>
                <td className="px-4 py-3 text-slate-400">{a.message}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.mac || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {acct.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">{t("common.none")}</td></tr>}
          </TableWrap>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TableWrap({ headers, children }) {
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#111827] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1F2937]">{headers.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
